/**
 * アクション定義の一元管理
 * 各アクションの設定（ラベル、カテゴリ、対応ページ、APIタイプ）を定義
 */

export const ACTIONS = {
  // 今日の詳細
  today_details: {
    label: '今日について詳しく知りたい',
    category: 'today',
    pages: ['purpose', 'function'],
    apiType: 'congestion',
  },
  
  // カレンダー系
  cal_holiday: {
    label: '定休日を検討したい',
    category: 'calendar',
    pages: ['purpose'],
    apiType: 'graph',
  },
  cal_shoping_holiday: {
    label: 'ショッピング施設の定休日を検討したい',
    category: 'calendar',
    pages: ['purpose'],
    apiType: 'graph',
  },
  cal_long_holiday: {
    label: '長期休暇を検討したい',
    category: 'calendar',
    pages: ['purpose'],
    apiType: 'graph',
  },
  cal_event: {
    label: 'イベント開催日を検討したい',
    category: 'calendar',
    pages: ['purpose'],
    apiType: 'graph',
  },
  cal_training: {
    label: '研修日を検討したい',
    category: 'calendar',
    pages: ['purpose'],
    apiType: 'graph',
  },
  cal_cog: {
    label: 'カレンダー形式で混雑度を見たい',
    category: 'calendar',
    pages: ['function'],
    apiType: 'graph',
  },
  
  // ヒートマップ系
  wti_cog: {
    label: '曜日×時間の混雑度を見たい',
    category: 'heatmap',
    pages: ['function'],
    apiType: 'graph',
  },
  wti_shift: {
    label: 'シフトを検討したい',
    category: 'heatmap',
    pages: ['purpose'],
    apiType: 'graph',
  },
  dti_cog: {
    label: '日付×時間の混雑度を見たい',
    category: 'heatmap',
    pages: ['function'],
    apiType: 'graph',
  },
  dti_open_hour: {
    label: '営業時間を検討したい',
    category: 'heatmap',
    pages: ['purpose'],
    apiType: 'graph',
  },
  dti_shoping_open_hour: {
    label: 'ショッピング施設の営業時間を検討したい',
    category: 'heatmap',
    pages: ['purpose'],
    apiType: 'graph',
  },
  dti_event_time: {
    label: 'イベント開催時間を検討したい',
    category: 'heatmap',
    pages: ['purpose'],
    apiType: 'graph',
  },
  
  // トレンド系
  month_trend: {
    label: '月別の傾向を見たい',
    category: 'trend',
    pages: ['function'],
    apiType: 'graph',
  },
  week_trend: {
    label: '週別の傾向を見たい',
    category: 'trend',
    pages: ['function'],
    apiType: 'graph',
  },
  year_trend: {
    label: '年別の傾向を見たい',
    category: 'trend',
    pages: ['function'],
    apiType: 'graph',
  },
  
  // イベント系
  event_effect: {
    label: 'イベント効果を分析したい',
    category: 'event',
    pages: ['purpose', 'function'],
    apiType: 'graph',
  },
  
  // 予測系: prediction_calendar は撤廃。
  // 当月以降の年/月選択時にカレンダー (cal_*) と日付×時間 (dti_*) へ予測が乗る。
  // 曜日×時間・トレンド・イベントには出さない。

  // 外国人系
  foreigners_distribution: {
    label: '外国人の国別分布を見たい',
    category: 'foreigners',
    pages: ['purpose', 'function'],
    apiType: 'foreigners',
  },
  foreigners_yearly_distribution: {
    label: '年間の外国人分布を見たい',
    category: 'foreigners',
    pages: ['purpose', 'function'],
    apiType: 'foreigners',
  },
};

/**
 * ページタイプに応じたアクション一覧を取得
 * @param {string} pageType - 'purpose' | 'function'
 * @returns {Array<Object>} アクション一覧
 */
export function getActionsForPage(pageType) {
  return Object.entries(ACTIONS)
    .filter(([, config]) => config.pages.includes(pageType))
    .map(([key, config]) => ({ id: key, ...config }));
}

/**
 * アクションがページで有効かチェック
 * @param {string} action - アクションID
 * @param {string} pageType - 'purpose' | 'function'
 * @returns {boolean} 有効かどうか
 */
export function isValidActionForPage(action, pageType) {
  return ACTIONS[action]?.pages.includes(pageType) ?? false;
}

/**
 * アクション設定を取得
 * @param {string} action - アクションID
 * @returns {Object|null} アクション設定
 */
export function getActionConfig(action) {
  return ACTIONS[action] || null;
}

const PREDICTION_CATEGORIES = new Set(['calendar']);
const PREDICTION_ACTION_IDS = new Set(['today_details']);

/** カレンダー・日付×時間・今日の詳細に予測を出す（曜日×時間は出さない） */
export function actionAllowsPrediction(action) {
  if (!action) return false;
  if (PREDICTION_ACTION_IDS.has(action)) return true;
  if (String(action).startsWith('dti_')) return true;
  return PREDICTION_CATEGORIES.has(ACTIONS[action]?.category);
}

/**
 * 場所選択が必要なアクションかどうかを判定
 * @param {string} action - アクションID
 * @returns {boolean} 場所選択が必要かどうか
 */
export function needsLocation(action) {
  const config = ACTIONS[action];
  // foreigners系のアクションは場所選択が不要
  return config?.apiType !== 'foreigners';
}
